import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  ShieldCheckIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import { useAuditStore } from '../../store/super-admin/auditSlice';
import { AuditService } from '../../services/super-admin/auditService';

const SuperAdminDashboard: React.FC = () => {
  const { stats, setStats, setLoading, setError } = useAuditStore();
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [securityAlerts, setSecurityAlerts] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch system stats
        const systemStats = await AuditService.getSystemStats();
        setStats(systemStats);

        // Fetch recent activity
        const activityData = await AuditService.getActivitySummary('today');
        setRecentActivity(activityData.topActions.slice(0, 5));

        // Fetch security alerts
        const alertsData = await AuditService.getSecurityAlerts('high');
        setSecurityAlerts(alertsData.alerts.slice(0, 3));

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [setStats, setLoading, setError]);

  const quickStats = [
    {
      name: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      change: '+12%',
      changeType: 'increase',
      icon: UsersIcon,
      href: '/super-admin/users',
      color: 'bg-blue-500'
    },
    {
      name: 'Active Tenants',
      value: stats.activeTenants.toLocaleString(),
      change: '+3',
      changeType: 'increase',
      icon: BuildingOfficeIcon,
      href: '/super-admin/tenants',
      color: 'bg-green-500'
    },
    {
      name: 'Total Content',
      value: stats.totalContent.toLocaleString(),
      change: '+8%',
      changeType: 'increase',
      icon: DocumentTextIcon,
      href: '/content',
      color: 'bg-purple-500'
    },
    {
      name: 'Active Campaigns',
      value: stats.totalCampaigns.toLocaleString(),
      change: '+5%',
      changeType: 'increase',
      icon: ChartBarIcon,
      href: '/campaigns',
      color: 'bg-orange-500'
    }
  ];

  const systemHealth = [
    {
      name: 'API Response Time',
      value: '145ms',
      status: 'good',
      icon: ServerIcon
    },
    {
      name: 'Database Performance',
      value: '98.5%',
      status: 'good',
      icon: ServerIcon
    },
    {
      name: 'Error Rate',
      value: '0.02%',
      status: 'good',
      icon: ExclamationTriangleIcon
    },
    {
      name: 'Security Score',
      value: '94/100',
      status: 'warning',
      icon: ShieldCheckIcon
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">Super Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          System overview and key metrics across all tenants
        </p>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {quickStats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow hover:shadow-md transition-shadow sm:px-6"
          >
            <dt>
              <div className={`absolute rounded-md ${stat.color} p-3`}>
                <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16 flex items-baseline">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p
                className={`ml-2 flex items-baseline text-sm font-semibold ${
                  stat.changeType === 'increase'
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}
              >
                {stat.change}
              </p>
            </dd>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* System Health */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">System Health</h3>
              <Link
                to="/super-admin/analytics"
                className="text-sm font-medium text-purple-600 hover:text-purple-500"
              >
                View Details
              </Link>
            </div>
            <div className="mt-6 space-y-4">
              {systemHealth.map((metric) => (
                <div key={metric.name} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <metric.icon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-900">{metric.name}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">
                      {metric.value}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(metric.status)}`}
                    >
                      {metric.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              <Link
                to="/super-admin/audit/logs"
                className="text-sm font-medium text-purple-600 hover:text-purple-500"
              >
                View All
              </Link>
            </div>
            <div className="mt-6 flow-root">
              <ul className="-my-5 divide-y divide-gray-200">
                {recentActivity.map((activity, index) => (
                  <li key={index} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <ClockIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {activity.action}
                        </p>
                        <p className="truncate text-sm text-gray-500">
                          {activity.count} times today
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Security Alerts */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Security Alerts</h3>
              <Link
                to="/super-admin/audit/security"
                className="text-sm font-medium text-purple-600 hover:text-purple-500"
              >
                View All
              </Link>
            </div>
            <div className="mt-6 space-y-4">
              {securityAlerts.length > 0 ? (
                securityAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {alert.title}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {alert.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {alert.timestamp}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <ShieldCheckIcon className="mx-auto h-8 w-8 text-green-400" />
                  <p className="mt-2 text-sm text-gray-500">No active security alerts</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Link
              to="/super-admin/users/create"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
            >
              <div className="flex-shrink-0">
                <UsersIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">Create User</p>
                <p className="text-sm text-gray-500 truncate">Add new user to system</p>
              </div>
            </Link>

            <Link
              to="/super-admin/tenants/create"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
            >
              <div className="flex-shrink-0">
                <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">Create Tenant</p>
                <p className="text-sm text-gray-500 truncate">Add new tenant account</p>
              </div>
            </Link>

            <Link
              to="/super-admin/bulk"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
            >
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">Bulk Operations</p>
                <p className="text-sm text-gray-500 truncate">Import/export users</p>
              </div>
            </Link>

            <Link
              to="/super-admin/audit/reports"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
            >
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">Generate Report</p>
                <p className="text-sm text-gray-500 truncate">System usage reports</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;